import * as d3 from 'd3';
import { Prediction } from '../../../types/predictions';
import { Theme } from '../../../theme';
import { formatPrice } from '../../../utils/priceUtils';
import { predictionStyles } from './predictionStyles';

interface RenderProps {
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>;
  predictions: Prediction[];
  activePredictions: number[];
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  theme: Theme;
  chartData?: Array<{ time: Date; value: number }>;
}

/**
 * Specialized renderer for successful predictions that adds visual elements
 * to highlight when and how a prediction was fulfilled
 */
export const renderSuccessfulPredictions = ({
  chartArea,
  predictions,
  activePredictions,
  xScale,
  yScale,
  theme,
  chartData = []
}: RenderProps): void => {
  // Skip rendering if no chart data is available
  if (chartData.length === 0) return;


  // Only render for active successful predictions
  const successfulPredictions = predictions.filter(p => 
    activePredictions.includes(p.id) && 
    p.success_status === 'success'
  );
  
  if (successfulPredictions.length === 0) {
    return;
  }
  
  
  // Create a group for successful prediction overlays if it doesn't exist
  let successGroup = chartArea.select<SVGGElement>('.successful-predictions-group');
  if (successGroup.empty()) {
    successGroup = chartArea.append('g')
      .attr('class', 'successful-predictions-group');
  }
  
  // Process each successful prediction to find crossing points
  const processedPredictions = successfulPredictions.map(prediction => {
    
    // Extract target price
    let targetPrice: number | null = null;
    if (prediction.prediction_type === 'target') {
      targetPrice = prediction.price_target !== undefined ? 
        Number(prediction.price_target) : null;
    } else if (prediction.prediction_type === 'range') {
      // For range, we use price_min as the target that was reached
      targetPrice = prediction.price_min !== undefined ? 
        Number(prediction.price_min) : null;
    } else if (prediction.prediction_type === 'pricetimerange') {
      targetPrice = prediction.price_min !== undefined ? 
        Number(prediction.price_min) : null;
    }
    
    if (!targetPrice) return null;
    
    // Get the valid time range for this prediction
    const startDate = typeof prediction.start_date === 'string' ? 
      (prediction.start_date ? new Date(prediction.start_date) : null) : prediction.start_date;
      
    const endDate = typeof prediction.end_date === 'string' ? 
      (prediction.end_date ? new Date(prediction.end_date) : null) : prediction.end_date;
    
    
    // Find the crossing point WITHIN the valid time range of the prediction
    let crossingPoint: { time: Date; value: number } | null = null;
    
    // Sort data chronologically
    const sortedData = [...chartData].sort((a, b) => 
      a.time.getTime() - b.time.getTime()
    );
    
    // Find where price crosses the target WITHIN the valid time range
    for (let i = 1; i < sortedData.length; i++) {
      const prev = sortedData[i-1];
      const curr = sortedData[i];
      
      // Skip if current point is outside the prediction's time range
      if (prediction.prediction_type === 'target') {
        if (startDate && curr.time < startDate) continue;
      } else {
        if ((startDate && curr.time < startDate) || (endDate && curr.time > endDate)) continue;
      }
      
      // Check if price crosses target between these points
      if ((prev.value < targetPrice && curr.value >= targetPrice) || 
          (prev.value > targetPrice && curr.value <= targetPrice)) {
        crossingPoint = curr;
        break;
      }
    }
    
    return {
      ...prediction,
      targetPrice,
      crossingPoint,
      startDate,
      endDate
    };
  }).filter(Boolean) as (Prediction & { 
    targetPrice: number; 
    crossingPoint: { time: Date; value: number } | null;
    startDate: Date | null;
    endDate: Date | null;
  })[];
  
  console.log('Successful predictions with crossings:', processedPredictions.filter(p => p.crossingPoint).length);
  
  // Create a unique key function for data joining
  const keyFn = (d: any) => `success-${d.id}`;
  
  // Add crossing point markers ONLY - larger and more visible
  const crossingMarkers = successGroup.selectAll<SVGCircleElement, any>('.crossing-marker')
    .data(processedPredictions.filter(p => p.crossingPoint), keyFn);
  
  // Enter new markers
  const enterMarkers = crossingMarkers.enter()
    .append('circle')
    .attr('class', 'crossing-marker')
    .attr('r', 8) // Large radius for visibility
    .attr('stroke', 'white')
    .attr('stroke-width', 2.5);
  
  // Update all markers
  const allMarkers = enterMarkers.merge(crossingMarkers as any)
    .attr('cx', d => xScale(d.crossingPoint!.time))
    .attr('cy', d => yScale(d.crossingPoint!.value))
    .attr('fill', predictionStyles.colors.successSolid);
  
  // Add pulse animation for better visibility
  chartArea.select('defs').selectAll('filter#pulse-animation').remove();
  const pulseFilter = chartArea.select('defs').append('filter')
    .attr('id', 'pulse-animation')
    .attr('x', '-50%')
    .attr('y', '-50%')
    .attr('width', '200%')
    .attr('height', '200%');
  
  pulseFilter.append('feGaussianBlur')
    .attr('in', 'SourceGraphic')
    .attr('stdDeviation', '4')
    .attr('result', 'blur');
  
  pulseFilter.append('feColorMatrix')
    .attr('in', 'blur')
    .attr('mode', 'matrix')
    .attr('values', '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7')
    .attr('result', 'glow');
  
  pulseFilter.append('feBlend')
    .attr('in', 'SourceGraphic')
    .attr('in2', 'glow')
    .attr('mode', 'normal');
  
  allMarkers.style('filter', 'url(#pulse-animation)')
    .style('opacity', 1);
  
  // Add mouseover/tap highlighting for mobile
  allMarkers
    .attr('data-prediction-id', d => d.id)
    .on('mouseover touchstart', function() {
      d3.select(this)
        .transition()
        .duration(300)
        .attr('r', 10);
    })
    .on('mouseout touchend', function() {
      d3.select(this)
        .transition()
        .duration(300)
        .attr('r', 8);
    });
  
  // Remove old markers
  crossingMarkers.exit().remove();
  
  // REMOVED: All "Verified" badge labels
  // REMOVED: All connector lines 
  // REMOVED: All "Target Reached" labels
  
  // Clean up any existing elements from previous versions
  successGroup.selectAll('.verified-label').remove();
  successGroup.selectAll('.success-connector').remove();
  successGroup.selectAll('.target-reached-label').remove();
  
};
